import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Table, TableBody, TableCell,
  TableHead, TableRow, Chip, Button, Alert, IconButton, TextField,
  Dialog, DialogTitle, DialogContent, DialogActions, Tooltip, Grid
} from '@mui/material';
import { Add, Edit, Delete, Home, AccountBalance, WarningAmber } from '@mui/icons-material';
import axios from 'axios';
import { useUnsavedGuard } from '../hooks/useUnsavedGuard';
import { useGLAccounts } from '../hooks/useGLAccounts';
import { glAccountOptions } from '../utils/glAccountOptions';

const API = process.env.REACT_APP_API_URL;
const emptyForm = { code: '', name: '', arAccount: '', advanceAccount: '', discountAccount: '' };

const LearnerPostingGroupList = () => {
  const navigate = useNavigate();
  const { accounts: glAccounts } = useGLAccounts();
  const [groups,     setGroups]     = useState([]);
  const [error,      setError]      = useState('');
  const [success,    setSuccess]    = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing,    setEditing]    = useState(null);
  const [form,       setForm]       = useState(emptyForm);
  const { attemptClose, discardDialog } = useUnsavedGuard({ open: dialogOpen, value: form, onClose: () => setDialogOpen(false) });
  const [saving,     setSaving]     = useState(false);
  const [confirmDlg, setConfirmDlg] = useState(null);

  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/academics/learner-posting-groups`, { headers });
      setGroups(data);
    } catch { setError('Failed to load posting groups'); }
  }, []); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  const arAccounts = glAccounts.filter(a => a.isActive);

  const openCreate = () => {
    setEditing(null); setForm(emptyForm); setError(''); setDialogOpen(true);
  };
  const openEdit = (g) => {
    setEditing(g);
    setForm({ code: g.code, name: g.name, arAccount: g.arAccount,
              advanceAccount: g.advanceAccount, discountAccount: g.discountAccount || '' });
    setError(''); setDialogOpen(true);
  };

  const handle = (e) => {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
  };

  const handleSave = async () => {
    if (!form.code.trim())           { setError('Code is required'); return; }
    if (!form.name.trim())           { setError('Name is required'); return; }
    if (!form.arAccount.trim())      { setError('AR Account is required'); return; }
    if (!form.advanceAccount.trim()) { setError('Advance Account is required'); return; }
    setSaving(true); setError('');
    try {
      if (editing) {
        await axios.put(`${API}/api/academics/learner-posting-groups/${editing.code}`,
          { name: form.name, arAccount: form.arAccount, advanceAccount: form.advanceAccount,
            discountAccount: form.discountAccount }, { headers });
        setSuccess('Updated');
      } else {
        await axios.post(`${API}/api/academics/learner-posting-groups`,
          { code: form.code.toUpperCase(), name: form.name, arAccount: form.arAccount,
            advanceAccount: form.advanceAccount, discountAccount: form.discountAccount }, { headers });
        setSuccess('Created');
      }
      setDialogOpen(false); load();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Save failed');
    } finally { setSaving(false); }
  };

  const handleDelete = (g) => {
    setConfirmDlg(g);
  };

  const confirmAction = async () => {
    try {
      await axios.delete(`${API}/api/academics/learner-posting-groups/${confirmDlg.code}`, { headers });
      setSuccess('Deleted');
      setConfirmDlg(null);
      load();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Delete failed');
      setConfirmDlg(null);
    }
  };

  const glLabel = (no) => {
    const a = glAccounts.find(x => x.accountNo === no);
    return a ? `${no} — ${a.accountName}` : no;
  };

  return (
    <Box sx={{ maxWidth: 860, mx: 'auto', mt: 4, mb: 4 }}>
      <Card elevation={3}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2} flexWrap="wrap" gap={1}>
            <Box display="flex" alignItems="center" gap={1}>
              <AccountBalance color="primary" />
              <Typography variant="h5" color="primary">Learner Posting Groups</Typography>
              <Chip label={groups.length} size="small" color="primary" sx={{ ml: 0.5 }} />
            </Box>
            <Box display="flex" gap={1}>
              <Tooltip title="Back to Setups">
                <IconButton onClick={() => navigate('/setups')}
                  sx={{ bgcolor: 'background.paper', '&:hover': { bgcolor: 'grey.100' } }}>
                  <Home />
                </IconButton>
              </Tooltip>
              <Button variant="contained" startIcon={<Add />} onClick={openCreate}>Add Group</Button>
            </Box>
          </Box>

          {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
          {error   && <Alert severity="error"   sx={{ mb: 2 }}>{error}</Alert>}

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Maps learner categories to GL accounts. Assign a group on the learner card; the system uses it to post AR and advance entries automatically.
          </Typography>

          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell><strong>Code</strong></TableCell>
                <TableCell><strong>Name</strong></TableCell>
                <TableCell><strong>AR Account</strong></TableCell>
                <TableCell><strong>Advance Account</strong></TableCell>
                <TableCell><strong>Discount Account</strong></TableCell>
                <TableCell align="right"><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {groups.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography variant="body2" color="text.secondary" py={2}>
                      No posting groups. Run <code>node backend/scripts/seed-posting-groups.js</code> or add manually.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : groups.map(g => (
                <TableRow key={g.code} hover>
                  <TableCell><strong>{g.code}</strong></TableCell>
                  <TableCell>{g.name}</TableCell>
                  <TableCell><Typography variant="caption" fontFamily="monospace">{glLabel(g.arAccount)}</Typography></TableCell>
                  <TableCell><Typography variant="caption" fontFamily="monospace">{glLabel(g.advanceAccount)}</Typography></TableCell>
                  <TableCell>
                    {g.discountAccount
                      ? <Typography variant="caption" fontFamily="monospace">{glLabel(g.discountAccount)}</Typography>
                      : <em style={{ color: '#bbb', fontSize: 12 }}>—</em>}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit">
                      <IconButton size="small" color="primary" onClick={() => openEdit(g)}><Edit fontSize="small" /></IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" color="error" onClick={() => handleDelete(g)}><Delete fontSize="small" /></IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* ── Confirm Delete dialog ── */}
      <Dialog open={Boolean(confirmDlg)} onClose={() => setConfirmDlg(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'error.main' }}>
          <WarningAmber color="error" /> Confirm Delete
        </DialogTitle>
        <DialogContent>
          <Typography>Delete posting group <strong>{confirmDlg?.code} — {confirmDlg?.name}</strong>?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDlg(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={confirmAction}>Delete</Button>
        </DialogActions>
      </Dialog>

      {discardDialog}
      <Dialog open={dialogOpen} onClose={attemptClose} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? `Edit — ${editing.code}` : 'Add Learner Posting Group'}</DialogTitle>
        <DialogContent dividers>
          {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <TextField label="Code *" name="code" value={form.code} onChange={handle}
                fullWidth size="small" disabled={!!editing}
                inputProps={{ style: { textTransform: 'uppercase' } }}
                helperText="e.g. BOARDING" />
            </Grid>
            <Grid item xs={8}>
              <TextField label="Name *" name="name" value={form.name}
                onChange={handle} fullWidth size="small" helperText="e.g. Boarding Learner" />
            </Grid>
            <Grid item xs={6}>
              <TextField select label="AR Account *" name="arAccount" value={form.arAccount}
                onChange={handle} fullWidth size="small" helperText="Accounts receivable">
                {glAccountOptions(arAccounts)}
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField select label="Advance Account *" name="advanceAccount" value={form.advanceAccount}
                onChange={handle} fullWidth size="small" helperText="Student advance receipts">
                {glAccountOptions(arAccounts)}
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField select label="Discount Account" name="discountAccount" value={form.discountAccount}
                onChange={handle} fullWidth size="small" helperText="Optional">
                {glAccountOptions(arAccounts)}
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={attemptClose}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : editing ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LearnerPostingGroupList;
